import React from 'react';
import { motion } from 'framer-motion';
import {
  FaBriefcase,
  FaLaptopCode,
  FaCogs,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaArrowRight,
  FaCode,
  FaDatabase,
  FaRobot,
  FaLayerGroup,
  FaShieldAlt,
  FaRocket,
  FaTerminal
} from 'react-icons/fa';
import '../css/Experience.css';

const experiences = [
  {
    year: '2026 - Now',
    icon: <FaCogs />,
    title: 'Helper - Low Voltage / BAS',
    company: 'Lotus Automation and Electrical Solution Inc.',
    location: 'Toronto, Canada',
    date: 'April 2026 - Present',
    accent: 'cyan',
    summary:
      'Supporting low-voltage and building automation installation work while connecting hands-on field exposure with software, networking, dashboards, and BAS learning workflows.',
    bullets: [
      'Support pulling, organizing, labeling, stripping, and preparing control wiring for sensors, controllers, panels, and building automation devices.',
      'Assist with sensor/controller wiring preparation, field troubleshooting, device identification, and point-level installation support.',
      'Apply software background to BACnet communication, TCP/IP concepts, Niagara/Workbench basics, YABE testing, Modbus concepts, and commissioning processes.',
      'Build BAS learning and simulation workflows using Python, Java/Maven, Node-RED, InfluxDB, and Grafana to visualize points, trends, alarms, and controller data flow.'
    ],
    tech: ['BAS', 'BACnet', 'Node-RED', 'Grafana', 'InfluxDB', 'Modbus', 'TCP/IP'],
    metric: 'Field + Software Bridge',
    signal: 'ACTIVE'
  },
  {
    year: '2024 - 2025',
    icon: <FaBriefcase />,
    title: 'Independent Software Engineer & Product Builder',
    company: 'Client Products, AI Tools, Mobile Apps, Business Websites',
    location: 'Toronto, Canada',
    date: 'October 2024 - December 2025',
    accent: 'blue',
    summary:
      'Built production-minded web, mobile, backend, and AI automation products across service businesses, client websites, and internal software workflows.',
    bullets: [
      'Built Mechanic Finders / OZ Mechanic, an iOS mobile mechanic service platform using React Native, FastAPI, MongoDB, JWT authentication, booking flows, vehicle records, service management, and admin workflows.',
      'Developed CutCut Barbershop, a premium mobile booking platform with customer/admin flows, service discovery, appointment scheduling, dashboard structure, payment-ready architecture, and TestFlight delivery.',
      'Delivered branded business websites and product interfaces for MTB Fabrics, WikyWings, BatmanInc, Zergroup, Park Clinic, Sword Garage, and First Nations business clients.',
      'Created AI and automation products including AI Code Tester, Voice Clone AI, and RamizKaraEski Personal AI Assistant using React, FastAPI, Ollama, LLM workflows, Python, integrations, intent classification, and voice model pipelines.'
    ],
    tech: ['React', 'React Native', 'FastAPI', 'MongoDB', 'Ollama', 'Python', 'JWT'],
    metric: 'Products + Client Systems',
    signal: 'BUILD'
  },
  {
    year: '2024',
    icon: <FaLaptopCode />,
    title: 'Backend Developer',
    company: 'TrioTech Web',
    location: 'Calgary, Canada',
    date: 'August 2024 - October 2024',
    accent: 'blue',
    summary:
      'Built secure backend services, business software, invoice systems, messaging tools, and API foundations for internal workflows.',
    bullets: [
      'Developed Flask and Express.js services with JWT authentication and MongoDB data models.',
      'Built invoice and messaging systems with role-based access control.',
      'Delivered maintainable REST APIs for eCommerce and business operations.'
    ],
    tech: ['Flask', 'Express.js', 'MongoDB', 'REST APIs'],
    metric: 'Backend + APIs',
    signal: 'SECURE'
  },
  {
    year: '2023 - 2024',
    icon: <FaLaptopCode />,
    title: 'Software Developer',
    company: 'Koyuncu Elektronik A.S.',
    location: 'Istanbul, Turkiye',
    date: 'December 2023 - August 2024',
    accent: 'violet',
    summary:
      'Improved commercial systems, B2B dealer portals, inventory flows, logistics integrations, and authenticated business platforms.',
    bullets: [
      'Revamped B2B portals with React.js and Node.js for stronger ordering workflows.',
      'Integrated logistics and inventory APIs to improve operational visibility.',
      'Designed secure access control for business-facing dealer systems.'
    ],
    tech: ['React.js', 'Node.js', 'APIs', 'Auth Systems'],
    metric: 'Commerce + Operations',
    signal: 'SCALE'
  },
  {
    year: '2023',
    icon: <FaLaptopCode />,
    title: 'Backend Developer',
    company: 'Ozkoyuncu Madencilik A.S.',
    location: 'Kayseri, Turkiye',
    date: 'April 2023 - December 2023',
    accent: 'cyan',
    summary:
      'Built backend APIs for industrial reporting, mining sensor data workflows, ERP modernization, inventory visibility, and shipment tracking.',
    bullets: [
      'Built backend APIs for industrial reporting and mining sensor data workflows.',
      'Optimized MongoDB queries, improving report generation speed by 35%.',
      'Automated Python-based quality control reporting and supported Express.js/SQL backend development for plant dashboard systems.'
    ],
    tech: ['Backend APIs', 'MongoDB', 'Python', 'Express.js', 'SQL', 'ERP'],
    metric: 'Data + Reporting',
    signal: 'DATA'
  }
];

const commandStats = [
  { icon: <FaCode />, label: 'Full Stack', value: 'Web + mobile' },
  { icon: <FaDatabase />, label: 'Backend', value: 'APIs + data' },
  { icon: <FaRobot />, label: 'AI Automation', value: 'LLM workflows' },
  { icon: <FaShieldAlt />, label: 'BAS Bridge', value: 'BACnet + field' }
];

const flowSteps = ['React UI', 'API Core', 'BAS Data', 'AI Logic', 'Mobile UX'];

const cardVariants = {
  hidden: { opacity: 0, y: 44, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const Experience = () => {
  return (
    <section id="experience" className="experience-section">
      <div className="experience-grid-lines" />
      <div className="experience-scan scan-a" />
      <div className="experience-scan scan-b" />
      <div className="experience-glow glow-a" />
      <div className="experience-glow glow-b" />

      <div className="experience-shell">
        <motion.div
          className="experience-command"
          initial={{ opacity: 0, x: -36 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.75 }}
        >
          <span className="experience-kicker">Experience OS</span>
          <h1>Career system view</h1>
          <p>
            Software engineer focused on web, mobile, backend, AI automation,
            and BAS-related software tools, with hands-on low-voltage field exposure.
          </p>

          <div className="experience-radar" aria-hidden="true">
            <div className="radar-ring ring-one" />
            <div className="radar-ring ring-two" />
            <div className="radar-ring ring-three" />
            <div className="radar-sweep" />
            <div className="radar-core">
              <FaRocket />
              <span>ONLINE</span>
            </div>
            {flowSteps.map((step, index) => (
              <span key={step} className={`radar-chip radar-chip-${index + 1}`}>
                {step}
              </span>
            ))}
          </div>

          <div className="experience-command-stats">
            {commandStats.map((item) => (
              <div className="command-stat" key={item.label}>
                <span>{item.icon}</span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.value}</small>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="experience-board">
          <motion.div
            className="experience-board-header"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.65 }}
          >
            <div>
              <span className="board-eyebrow">
                <FaTerminal />
                Work timeline
              </span>
              <h2>Experience</h2>
            </div>
            <div className="board-status">
              <span className="status-dot" />
              Available for product teams
            </div>
          </motion.div>

          <motion.div
            className="experience-list"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.08 }}
            transition={{ staggerChildren: 0.1 }}
          >
            {experiences.map((job, index) => (
              <motion.article
                key={job.title + job.company}
                className={`experience-row accent-${job.accent}`}
                variants={cardVariants}
                whileHover={{ y: -6, transition: { duration: 0.22 } }}
              >
                <div className="row-index">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <i />
                </div>

                <div className="row-main">
                  <div className="row-top">
                    <div className="row-icon">{job.icon}</div>
                    <div className="row-title">
                      <span className="row-company">{job.company}</span>
                      <h3>{job.title}</h3>
                    </div>
                    <div className="row-signal">{job.signal}</div>
                  </div>

                  <div className="row-meta">
                    <span><FaCalendarAlt /> {job.date}</span>
                    <span><FaMapMarkerAlt /> {job.location}</span>
                    <span><FaLayerGroup /> {job.metric}</span>
                  </div>

                  <p className="row-summary">{job.summary}</p>

                  <div className="row-detail-grid">
                    <ul>
                      {job.bullets.map((item) => (
                        <li key={item}>
                          <span className="bullet-light" />
                          {item}
                        </li>
                      ))}
                    </ul>

                    <div className="row-tech">
                      {job.tech.map((tech) => (
                        <span key={tech}>{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="row-year">
                  <strong>{job.year}</strong>
                  <FaArrowRight />
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
